/**
 * Day 6, audience signals and comparative measurements, from signal
 * extraction onward: the deterministic classification, the Owner's label
 * review, the comparative run, the signal and measurement history, the
 * manifest, and the comparative-analysis artifact.
 */

import type { ReactNode } from 'react';

import { ANALYSIS_STAGES } from '@mouja/domain/analysis';

import {
  extractAnalysisAudienceSignals,
  newIdempotencyKey,
  reviewAnalysisAudienceSignals,
  runAnalysisComparisons,
  type AnalysisDay6State,
  type AnalysisRunSummary,
} from '../../api.js';
import { Button, InlineAlert, Tag, TextAreaField } from '../../ui.js';
import { SectionHeading } from '../common.js';
import { parseObjectJson } from './review-json.js';
import { card } from './run-page-styles.js';
import { toneFor } from './run-state-labels.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { ReviewDrafts } from './useReviewDrafts.js';

export function AudienceComparisonSection({
  id,
  run,
  day6,
  busy,
  mutate,
  drafts,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly day6: AnalysisDay6State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly drafts: ReviewDrafts;
}): ReactNode {
  const { audienceReviewJson, setAudienceReviewJson } = drafts;
  if (
    ANALYSIS_STAGES.findIndex((stage) => stage === run.currentStage) <
    ANALYSIS_STAGES.indexOf('AUDIENCE_SIGNAL_EXTRACTION')
  ) {
    return null;
  }

  return (
    <section id="audience-comparison" style={{ marginBottom: 'var(--space-lg)' }}>
      <SectionHeading>Audience signals and comparative measurements</SectionHeading>
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
          Measurements describe this bounded corpus and sampled comments only. They are
          not findings, conversion rates, causal claims, or market benchmarks.
        </p>
        {run.currentStage === 'AUDIENCE_SIGNAL_EXTRACTION' &&
        run.executionState === 'PENDING' ? (
          <Button
            variant="primary"
            busy={busy === 'audience-extract'}
            onClick={() =>
              void mutate('audience-extract', () =>
                extractAnalysisAudienceSignals(id, newIdempotencyKey()),
              )
            }
          >
            Classify sampled comments with deterministic rules
          </Button>
        ) : null}

        {run.currentStage === 'AUDIENCE_SIGNAL_EXTRACTION' &&
        run.executionState === 'AWAITING_REVIEW' ? (
          <>
            <InlineAlert
              tone="warning"
              title="Review every proposed sampled-comment label before measurements are calculated."
            />
            <TextAreaField
              label="Audience-signal review JSON"
              value={audienceReviewJson}
              onChange={setAudienceReviewJson}
              rows={10}
              maxLength={1_800_000}
            />
            <p style={{ color: 'var(--text-muted)', margin: 0 }}>
              Every proposal is prefilled as APPROVED. To change one, use CORRECTED and
              add a labels array. NONE, UNKNOWN and SPAM_OR_IRRELEVANT must stand alone.
            </p>
            <Button
              variant="primary"
              busy={busy === 'audience-review'}
              disabled={!audienceReviewJson.trim()}
              onClick={() =>
                void mutate('audience-review', () =>
                  reviewAnalysisAudienceSignals(
                    id,
                    newIdempotencyKey(),
                    parseObjectJson(audienceReviewJson, 'Audience review'),
                  ),
                )
              }
            >
              Record reviewed labels and calculate sampled-comment measurements
            </Button>
          </>
        ) : null}

        {run.currentStage === 'COMPARATIVE_ANALYSIS' &&
        run.executionState === 'PENDING' ? (
          <Button
            variant="primary"
            busy={busy === 'comparative-run'}
            onClick={() =>
              void mutate('comparative-run', () =>
                runAnalysisComparisons(id, newIdempotencyKey()),
              )
            }
          >
            Run deterministic comparative measurements
          </Button>
        ) : null}
      </div>

      {day6.signals.length > 0 ? (
        <details style={{ ...card, marginTop: 12 }} open>
          <summary>Sampled-comment signal history ({day6.signals.length})</summary>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {day6.signals.map((signal) => (
              <article key={signal.id} style={card}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Tag tone={toneFor(signal.reviewState)}>{signal.reviewState}</Tag>
                  <Tag tone="info">v{signal.signalVersion}</Tag>
                  <Tag tone="info">{signal.annotationBasis}</Tag>
                  {signal.labels.map((label) => (
                    <Tag key={label} tone={label === 'UNKNOWN' ? 'warning' : 'info'}>
                      {label}
                    </Tag>
                  ))}
                </div>
                <strong>{signal.businessName}</strong>
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  <bdi>{signal.originalText}</bdi>
                </p>
                <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
                  Comment {signal.sampledCommentId} · content{' '}
                  <a href={signal.canonicalUrl} target="_blank" rel="noreferrer">
                    {signal.contentItemId}
                  </a>{' '}
                  · prior version {signal.priorSignalVersionId ?? 'none'}
                </p>
              </article>
            ))}
          </div>
        </details>
      ) : null}

      {day6.measurements.length > 0 ? (
        <details style={{ ...card, marginTop: 12 }} open>
          <summary>
            Per-post sampled-comment measurements ({day6.measurements.length})
          </summary>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {day6.measurements.map((measurement) => (
              <article key={measurement.id} style={card}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Tag tone="info">{measurement.subjectRole}</Tag>
                  <Tag tone="info">v{measurement.measurementVersion}</Tag>
                </div>
                <strong>{measurement.businessName}</strong>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(measurement.payload, null, 2)}
                </pre>
              </article>
            ))}
          </div>
        </details>
      ) : null}

      {day6.audienceManifest ? (
        <InlineAlert
          tone="success"
          title="Audience-signal manifest complete. Raw and analyzable denominators are preserved."
        />
      ) : null}

      {day6.comparativeAnalysis ? (
        <details style={{ ...card, marginTop: 12 }} open>
          <summary>Deterministic comparative-analysis artifact</summary>
          <p style={{ color: 'var(--text-muted)' }}>
            Input hash:{' '}
            {typeof day6.comparativeAnalysis.payload['inputHash'] === 'string'
              ? day6.comparativeAnalysis.payload['inputHash']
              : 'unavailable'}
          </p>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>Coverage</h3>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(
              day6.comparativeAnalysis.payload['coverage'] ?? {},
              null,
              2,
            )}
          </pre>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>
            Target versus competitor prevalence
          </h3>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(
              day6.comparativeAnalysis.payload['targetCompetitorComparison'] ?? [],
              null,
              2,
            )}
          </pre>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>
            Feature-associated measurements
          </h3>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(
              day6.comparativeAnalysis.payload['featureAssociatedSummaries'] ?? [],
              null,
              2,
            )}
          </pre>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>Comparability warnings</h3>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(
              day6.comparativeAnalysis.payload['comparabilityWarnings'] ?? [],
              null,
              2,
            )}
          </pre>
        </details>
      ) : null}
    </section>
  );
}
